import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api/operations';

function authApi() {
  const token = localStorage.getItem('dwi_token');
  const inst = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 15000,
  });
  inst.interceptors.response.use(
    r => r,
    e => {
      if (e.response?.status === 401) {
        localStorage.removeItem('dwi_token');
        localStorage.removeItem('dwi_user');
        window.location.href = '/login';
        return;
      }
      return Promise.reject(e);
    }
  );
  return inst;
}

// ── small helpers ────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#06C8E8', icon = '' }) {
  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 mb-1">{label}</p>
          <p className="text-xl font-bold" style={{ color }}>{icon && <span className="mr-1">{icon}</span>}{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function Badge({ status }) {
  const colors = {
    up: 'bg-green-900 text-green-300', down: 'bg-red-900 text-red-300',
    connected: 'bg-green-900 text-green-300', disconnected: 'bg-red-900 text-red-300',
    operational: 'bg-green-900 text-green-300', degraded: 'bg-yellow-900 text-yellow-300',
    fast: 'bg-green-900 text-green-300', normal: 'bg-blue-900 text-blue-300', slow: 'bg-red-900 text-red-300',
    critical: 'bg-red-900 text-red-300', warning: 'bg-yellow-900 text-yellow-300', info: 'bg-blue-900 text-blue-300',
    error: 'bg-red-900 text-red-300', warn: 'bg-yellow-900 text-yellow-300',
  };
  const cls = colors[status] || 'bg-gray-800 text-gray-300';
  return <span className={'px-2 py-0.5 rounded text-xs font-semibold ' + cls}>{status}</span>;
}

function MiniBar({ pct, color = '#06C8E8' }) {
  return (
    <div className="h-2 bg-gray-800 rounded-full overflow-hidden w-full">
      <div className="h-2 rounded-full transition-all duration-500" style={{ width: Math.min(pct, 100) + '%', background: color }} />
    </div>
  );
}

function SectionHeader({ title, onRefresh, loading }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base font-semibold text-white">{title}</h2>
      {onRefresh && (
        <button onClick={onRefresh} disabled={loading}
          className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      )}
    </div>
  );
}

// ── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',       label: 'Overview' },
  { id: 'payments',       label: 'Payments' },
  { id: 'financial',      label: 'Financial' },
  { id: 'security',       label: 'Security' },
  { id: 'logs',           label: 'Logs' },
  { id: 'backup',         label: 'Backup' },
  { id: 'notifications',  label: 'Notifications' },
  { id: 'performance',    label: 'Performance' },
];

// ════════════════════════════════════════════════════════════
// TAB: Overview
// ════════════════════════════════════════════════════════════
function OverviewTab() {
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authApi();
      const [h, s] = await Promise.all([
        api.get('/health').then(r => r.data.data),
        api.get('/summary').then(r => r.data.data),
      ]);
      setHealth(h); setSummary(s);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (loading && !health) return <div className="text-gray-400 p-4 text-center">Loading system health...</div>;

  return (
    <div className="space-y-6">
      <SectionHeader title="System Health" onRefresh={load} loading={loading} />

      {/* Status row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-3">
          <div className={'w-3 h-3 rounded-full ' + (health?.database?.status === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-red-400')} />
          <div>
            <p className="text-xs text-gray-400">Database</p>
            <Badge status={health?.database?.status || 'unknown'} />
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
          <div>
            <p className="text-xs text-gray-400">API Server</p>
            <Badge status="up" />
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Process Uptime</p>
          <p className="text-sm font-semibold text-white">{health?.uptime?.process || '--'}</p>
          <p className="text-xs text-gray-500">OS: {health?.uptime?.system || '--'}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Version</p>
          <p className="text-sm font-semibold text-cyan-400">{health?.version || '--'}</p>
          <p className="text-xs text-gray-500">{health?.nodeVersion || ''}</p>
        </div>
      </div>

      {/* Memory & CPU */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Memory</p>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-400">
              <span>Heap Used</span>
              <span>{health?.memory?.heapUsed || 0} MB / {health?.memory?.heapTotal || 0} MB</span>
            </div>
            <MiniBar pct={(health?.memory?.heapUsed / health?.memory?.heapTotal) * 100 || 0} color="#06C8E8" />
            <div className="flex justify-between text-xs text-gray-400">
              <span>System RAM</span>
              <span>{health?.memory?.osUsedPct || 0}% used</span>
            </div>
            <MiniBar pct={health?.memory?.osUsedPct || 0} color={health?.memory?.osUsedPct > 80 ? '#EF4444' : '#10B981'} />
            <p className="text-xs text-gray-500">RSS: {health?.memory?.rss || 0} MB | OS Free: {health?.memory?.osFree || 0} MB</p>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">CPU & Disk</p>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-400">
              <span>CPU Load (1m avg, {health?.cpu?.cores || 1} cores)</span>
              <span>{health?.cpu?.loadPct || 0}%</span>
            </div>
            <MiniBar pct={health?.cpu?.loadPct || 0} color={health?.cpu?.loadPct > 80 ? '#EF4444' : '#F59E0B'} />
            <p className="text-xs text-gray-500">5m: {health?.cpu?.load5} | 15m: {health?.cpu?.load15}</p>
            <div className="border-t border-gray-800 pt-2 text-xs text-gray-400 space-y-1">
              <div className="flex justify-between"><span>Disk Total</span><span>{health?.disk?.total}</span></div>
              <div className="flex justify-between"><span>Disk Used</span><span>{health?.disk?.used} ({health?.disk?.pct})</span></div>
              <div className="flex justify-between"><span>Disk Free</span><span>{health?.disk?.free}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Key counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Users" value={summary?.counts?.totalUsers?.toLocaleString() || '0'} icon="👥" color="#06C8E8" />
        <StatCard label="Pending KYC" value={summary?.counts?.pendingKyc || '0'} icon="🪪" color={summary?.counts?.pendingKyc > 10 ? '#EF4444' : '#10B981'} />
        <StatCard label="Pending Withdrawals" value={summary?.counts?.pendingWithdrawals || '0'} icon="💸" color={summary?.counts?.pendingWithdrawals > 20 ? '#EF4444' : '#F59E0B'} />
        <StatCard label="Tx Today" value={summary?.counts?.txToday?.toLocaleString() || '0'} icon="📊" color="#8B5CF6" />
      </div>

      {/* Alerts */}
      {summary?.alerts?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Alerts</p>
          {summary.alerts.map((a, i) => (
            <div key={i} className={'rounded-xl px-4 py-3 flex items-start gap-3 ' + (a.severity === 'critical' ? 'bg-red-950 border border-red-900' : a.severity === 'warning' ? 'bg-yellow-950 border border-yellow-900' : 'bg-blue-950 border border-blue-900')}>
              <span className="text-lg">{a.severity === 'critical' ? '🚨' : a.severity === 'warning' ? '⚠️' : 'ℹ️'}</span>
              <div>
                <p className="text-sm font-semibold text-white">{a.title}</p>
                <p className="text-xs text-gray-300 mt-0.5">{a.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Server info */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Server Info</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          {[['Hostname', health?.hostname],['Platform', health?.platform],['Node', health?.nodeVersion],['Checked', health?.checkedAt ? new Date(health.checkedAt).toLocaleTimeString() : '--']].map(([k,v]) => (
            <div key={k}><p className="text-gray-500">{k}</p><p className="text-gray-200 font-mono">{v || '--'}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Payments
// ════════════════════════════════════════════════════════════
function PaymentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/payments');
      setData(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-gray-400 p-4">Loading payment data...</div>;

  const tx = data?.transactions || {};
  const wd = data?.withdrawals || {};
  const cd = data?.cryptoDeposits || {};
  const cw = data?.cryptoWithdrawals || {};
  const momo = data?.momo || {};

  const fmt = (n) => (n || 0).toLocaleString();

  return (
    <div className="space-y-6">
      <SectionHeader title="Payment Monitoring" onRefresh={load} loading={loading} />

      {/* Transaction summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Pending Deposits" value={fmt(tx.pending?.count)} sub={'UGX ' + fmt(tx.pending?.amount)} color="#F59E0B" icon="⏳" />
        <StatCard label="Completed" value={fmt(tx.completed?.count)} sub={'UGX ' + fmt(tx.completed?.amount)} color="#10B981" icon="✅" />
        <StatCard label="Failed Transactions" value={fmt(tx.failed?.count)} color="#EF4444" icon="❌" />
        <StatCard label="Processing" value={fmt(tx.processing?.count)} color="#06C8E8" icon="🔄" />
      </div>

      {/* Withdrawals */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Withdrawals</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['pending','approved','processing','completed','rejected','failed'].map(s => (
            <div key={s} className="bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-gray-400 capitalize mb-1">{s}</p>
              <p className="text-base font-bold text-white">{fmt(wd[s]?.count)}</p>
              {wd[s]?.amount > 0 && <p className="text-xs text-gray-500">UGX {fmt(wd[s]?.amount)}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Crypto */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Crypto Deposits (USDT)</p>
          <div className="space-y-2">
            {Object.entries(cd).length === 0 && <p className="text-gray-500 text-sm">No data</p>}
            {Object.entries(cd).map(([s, count]) => (
              <div key={s} className="flex justify-between items-center text-sm">
                <span className="text-gray-400 capitalize">{s}</span>
                <Badge status={s} />
                <span className="text-white font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Crypto Withdrawals (USDT)</p>
          <div className="space-y-2">
            {Object.entries(cw).length === 0 && <p className="text-gray-500 text-sm">No data</p>}
            {Object.entries(cw).map(([s, v]) => (
              <div key={s} className="flex justify-between items-center text-sm">
                <span className="text-gray-400 capitalize">{s}</span>
                <span className="text-white">{fmt(v?.count)} — {(v?.amount || 0).toFixed(2)} USDT</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Money */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Mobile Money</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(momo).map(([provider, v]) => (
            <div key={provider} className="bg-gray-800 rounded-lg p-3">
              <p className="text-sm font-semibold text-white mb-1">{provider === 'mtn_momo' ? 'MTN MoMo' : provider === 'airtel_money' ? 'Airtel Money' : provider}</p>
              <p className="text-xs text-gray-400">Count: {fmt(v.count)} | Amount: UGX {fmt(v.amount)}</p>
            </div>
          ))}
          {Object.keys(momo).length === 0 && <p className="text-gray-500 text-sm">No MoMo data</p>}
        </div>
      </div>

      {/* Today */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-6">
        <div><p className="text-xs text-gray-400">Transactions Today</p><p className="text-xl font-bold text-cyan-400">{fmt(data?.today?.transactions)}</p></div>
        <div><p className="text-xs text-gray-400">Withdrawals Today</p><p className="text-xl font-bold text-purple-400">{fmt(data?.today?.withdrawals)}</p></div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Financial
// ════════════════════════════════════════════════════════════
function FinancialTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/financial');
      setData(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-gray-400 p-4">Loading financial data...</div>;

  const fmt = (n, dec = 0) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const byCode = (arr) => {
    const map = {};
    for (const r of (arr || [])) map[r._id] = r.balance;
    return map;
  };

  const cust  = byCode(data?.customerBalances);
  const treas = byCode(data?.treasuryBalances);

  const revenueTotal = (arr) => {
    const map = {};
    for (const r of (arr || [])) map[r._id] = r.amount;
    return map;
  };

  const revToday = revenueTotal(data?.revenue?.today);
  const revWeek  = revenueTotal(data?.revenue?.week);
  const revMonth = revenueTotal(data?.revenue?.month);

  return (
    <div className="space-y-6">
      <SectionHeader title="Financial Overview" onRefresh={load} loading={loading} />

      {/* Customer balances */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Total Customer Balances</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(cust).length === 0 && <p className="text-gray-500 text-sm">No data</p>}
          {Object.entries(cust).map(([cur, bal]) => (
            <div key={cur} className="bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">{cur}</p>
              <p className="text-lg font-bold text-cyan-400">{cur === 'USDT' ? fmt(bal, 2) : fmt(bal)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Treasury/Float */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Company Float (Treasury)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(treas).length === 0 && <p className="text-gray-500 text-sm">No treasury wallets</p>}
          {Object.entries(treas).map(([cur, bal]) => (
            <div key={cur} className={'bg-gray-800 rounded-lg p-3 ' + (bal < (cur === 'USDT' ? 100 : 1000000) ? 'border border-red-800' : '')}>
              <p className="text-xs text-gray-400 mb-1">{cur} {bal < (cur === 'USDT' ? 100 : 1000000) ? '⚠️' : ''}</p>
              <p className={'text-lg font-bold ' + (bal < (cur === 'USDT' ? 100 : 1000000) ? 'text-red-400' : 'text-green-400')}>
                {cur === 'USDT' ? fmt(bal, 2) : fmt(bal)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Revenue (Fees + Spread)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-800">
                <th className="text-left pb-2">Currency</th>
                <th className="text-right pb-2">Today</th>
                <th className="text-right pb-2">7 Days</th>
                <th className="text-right pb-2">30 Days</th>
              </tr>
            </thead>
            <tbody>
              {['UGX','USDT','USD'].map(cur => (
                <tr key={cur} className="border-b border-gray-800 last:border-0">
                  <td className="py-2 text-gray-300 font-medium">{cur}</td>
                  <td className="py-2 text-right text-cyan-400">{fmt(revToday[cur] || 0, cur === 'USDT' ? 2 : 0)}</td>
                  <td className="py-2 text-right text-white">{fmt(revWeek[cur] || 0, cur === 'USDT' ? 2 : 0)}</td>
                  <td className="py-2 text-right text-white">{fmt(revMonth[cur] || 0, cur === 'USDT' ? 2 : 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Exchange + investments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Exchange (Today)</p>
          <p className="text-2xl font-bold text-purple-400">{data?.exchange?.today?.count || 0} <span className="text-sm text-gray-400">orders</span></p>
          <p className="text-sm text-gray-400">Volume: {fmt(data?.exchange?.today?.volume || 0)} UGX</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">KYC Pending</p>
          <p className={'text-2xl font-bold ' + ((data?.kycPending || 0) > 10 ? 'text-red-400' : 'text-green-400')}>{data?.kycPending || 0}</p>
          <p className="text-sm text-gray-400">applications awaiting review</p>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Security
// ════════════════════════════════════════════════════════════
function SecurityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/security');
      setData(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-gray-400 p-4">Loading security data...</div>;

  return (
    <div className="space-y-6">
      <SectionHeader title="Security Monitor" onRefresh={load} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Audit Events (24h)" value={data?.stats?.auditEvents24h || 0} icon="📋" color="#06C8E8" />
        <StatCard label="New Users (24h)" value={data?.stats?.newUsers24h || 0} icon="👤" color="#10B981" />
        <StatCard label="Total Users" value={(data?.stats?.totalUsers || 0).toLocaleString()} icon="👥" color="#8B5CF6" />
        <StatCard label="Security Events" value={data?.stats?.securityEvents || 0} icon="🔒" color={data?.stats?.securityEvents > 0 ? '#EF4444' : '#10B981'} />
      </div>

      {/* Security logs */}
      {data?.securityLogs?.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Security Events (Last 24h)</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {data.securityLogs.map(log => (
              <div key={log._id} className="flex gap-3 text-xs bg-gray-800 rounded px-3 py-2">
                <span className="text-gray-500 shrink-0">{new Date(log.createdAt).toLocaleTimeString()}</span>
                <Badge status={log.level} />
                <span className="text-gray-400 shrink-0">{log.module}</span>
                <span className="text-gray-300 flex-1 truncate">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent audit log */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Recent Admin Actions (24h)</p>
        {(!data?.recentAuditLogs || data.recentAuditLogs.length === 0) && (
          <p className="text-gray-500 text-sm">No audit events in last 24 hours</p>
        )}
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {(data?.recentAuditLogs || []).map(log => (
            <div key={log._id} className="flex gap-3 text-xs border-b border-gray-800 pb-1">
              <span className="text-gray-500 shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
              <span className="text-cyan-400 shrink-0 font-mono">{log.action}</span>
              <span className="text-gray-300 flex-1 truncate">{log.description || log.model || ''}</span>
              <span className="text-gray-600 shrink-0">{log.actorEmail || ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Logs
// ════════════════════════════════════════════════════════════
function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ level: '', module: '', resolved: '', search: '' });
  const [newLog, setNewLog] = useState({ level: 'info', module: 'Manual', message: '' });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.level)    params.level = filters.level;
      if (filters.module)   params.module = filters.module;
      if (filters.resolved !== '') params.resolved = filters.resolved;
      if (filters.search)   params.search = filters.search;
      const r = await authApi().get('/logs', { params });
      setLogs(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await authApi().patch('/logs/' + id + '/resolve');
      load();
    } catch {}
  };

  const createLog = async () => {
    if (!newLog.message) return;
    try {
      await authApi().post('/logs', newLog);
      setNewLog({ level: 'info', module: 'Manual', message: '' });
      setShowForm(false);
      load();
    } catch {}
  };

  const levels = ['', 'info', 'warn', 'error', 'critical'];
  const levelColor = { info: 'text-blue-400', warn: 'text-yellow-400', error: 'text-red-400', critical: 'text-red-300 font-bold' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionHeader title={'Error Logs (' + total + ' total)'} onRefresh={load} loading={loading} />
        <button onClick={() => setShowForm(f => !f)} className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded">
          {showForm ? 'Cancel' : '+ Log Entry'}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Level</label>
              <select value={newLog.level} onChange={e => setNewLog(l => ({ ...l, level: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none">
                {['info','warn','error','critical'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Module</label>
              <input type="text" value={newLog.module} onChange={e => setNewLog(l => ({ ...l, module: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Message</label>
            <textarea value={newLog.message} onChange={e => setNewLog(l => ({ ...l, message: e.target.value }))} rows={2}
              className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none" />
          </div>
          <button onClick={createLog} className="px-4 py-1.5 bg-green-700 hover:bg-green-600 rounded text-white text-xs font-semibold">Submit Log</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {levels.map(l => (
          <button key={l} onClick={() => setFilters(f => ({ ...f, level: l }))}
            className={'px-2 py-1 text-xs rounded ' + (filters.level === l ? 'bg-cyan-700 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700')}>
            {l || 'All Levels'}
          </button>
        ))}
        <button onClick={() => setFilters(f => ({ ...f, resolved: f.resolved === '' ? 'false' : f.resolved === 'false' ? 'true' : '' }))}
          className={'px-2 py-1 text-xs rounded ' + (filters.resolved !== '' ? 'bg-cyan-700 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700')}>
          {filters.resolved === 'false' ? 'Unresolved' : filters.resolved === 'true' ? 'Resolved' : 'All Status'}
        </button>
        <input type="text" placeholder="Search..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs text-white focus:outline-none focus:border-cyan-500" />
      </div>

      {/* Log table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {logs.length === 0 && <p className="text-gray-500 text-sm p-4">No logs found. The system logs errors and warnings automatically.</p>}
        <div className="max-h-[600px] overflow-y-auto">
          {logs.map(log => (
            <div key={log._id} className={'flex gap-3 items-start px-4 py-3 border-b border-gray-800 last:border-0 ' + (log.resolved ? 'opacity-50' : '')}>
              <span className="text-xs text-gray-500 shrink-0 pt-0.5 w-32">{new Date(log.createdAt).toLocaleString()}</span>
              <span className={'text-xs shrink-0 uppercase font-mono w-14 ' + (levelColor[log.level] || 'text-gray-400')}>{log.level}</span>
              <span className="text-xs text-gray-500 shrink-0 w-24 truncate">{log.module}</span>
              <span className="text-xs text-gray-300 flex-1 min-w-0">{log.message}</span>
              <span className="text-xs text-gray-600 shrink-0 hidden md:block">{log.userEmail}</span>
              {!log.resolved && (
                <button onClick={() => resolve(log._id)} className="text-xs text-green-400 hover:text-green-300 shrink-0">Resolve</button>
              )}
              {log.resolved && <span className="text-xs text-green-600 shrink-0">Resolved</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Backup
// ════════════════════════════════════════════════════════════
function BackupTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [result, setResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/backup');
      setData(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const trigger = async () => {
    if (!window.confirm('Start a database backup now? This may take a few minutes.')) return;
    setTriggering(true);
    setResult('');
    try {
      const r = await authApi().post('/backup/trigger');
      setResult(r.data.message || 'Backup started');
    } catch (e) {
      setResult('Error: ' + (e.response?.data?.message || e.message));
    }
    setTriggering(false);
  };

  return (
    <div className="space-y-6">
      <SectionHeader title="Backup Center" onRefresh={load} loading={loading} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Backup Status</p>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-400">Backup Directory</span><span className="text-gray-200 font-mono text-xs">{data?.backupDir || '/home/dwi/backups'}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Total Size</span><span className="text-white font-semibold">{data?.totalSize || 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">MongoDB URI</span><span className={'font-semibold ' + (data?.mongoUri === 'configured' ? 'text-green-400' : 'text-red-400')}>{data?.mongoUri || 'Not configured'}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Status</span><Badge status={data?.status === 'available' ? 'operational' : 'info'} /></div>
          </div>
          <button onClick={trigger} disabled={triggering}
            className="w-full py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 rounded text-white text-sm font-semibold mt-4">
            {triggering ? 'Starting backup...' : 'Trigger Manual Backup'}
          </button>
          {result && <p className={'text-xs mt-2 p-2 rounded ' + (result.startsWith('Error') ? 'bg-red-950 text-red-300' : 'bg-green-950 text-green-300')}>{result}</p>}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Latest Code Version</p>
          <p className="font-mono text-xs text-gray-300 break-all">{data?.latestCommit || 'Unknown'}</p>
          <div className="border-t border-gray-800 pt-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recent Backups</p>
            <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap">{data?.lastBackupList || 'No backups found'}</pre>
          </div>
        </div>
      </div>

      <div className="bg-yellow-950 border border-yellow-900 rounded-xl p-4 text-xs text-yellow-300">
        <strong>Note:</strong> Backup uses <code className="font-mono bg-yellow-900 px-1 rounded">mongodump</code> to export the database to <code className="font-mono bg-yellow-900 px-1 rounded">/home/dwi/backups/</code>. Ensure this directory exists and has sufficient disk space. For production, configure automated backups via cron.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Notifications
// ════════════════════════════════════════════════════════════
function NotificationsTab() {
  const [data, setData] = useState({ data: [], alerts: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/notifications');
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await authApi().patch('/notifications/' + id + '/read');
      load();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await authApi().patch('/notifications/read-all');
      load();
    } catch {}
  };

  const severityIcon = { critical: '🚨', warning: '⚠️', info: 'ℹ️' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-white">Notifications</h2>
        <div className="flex gap-2">
          <button onClick={markAllRead} className="text-xs text-gray-400 hover:text-white">Mark all read</button>
          <button onClick={load} disabled={loading} className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {/* Live alerts */}
      {data.alerts?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Live System Alerts</p>
          {data.alerts.map((a, i) => (
            <div key={i} className={'rounded-xl px-4 py-3 flex items-start gap-3 ' + (a.severity === 'critical' ? 'bg-red-950 border border-red-900' : a.severity === 'warning' ? 'bg-yellow-950 border border-yellow-900' : 'bg-blue-950 border border-blue-900')}>
              <span className="text-lg shrink-0">{severityIcon[a.severity] || 'ℹ️'}</span>
              <div>
                <p className="text-sm font-semibold text-white">{a.title}</p>
                <p className="text-xs text-gray-300 mt-0.5">{a.message}</p>
              </div>
              <Badge status={a.severity} />
            </div>
          ))}
        </div>
      )}

      {/* Stored notifications */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Admin Notifications ({data.unreadCount || 0} unread)</p>
        {(!data.data || data.data.length === 0) && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No unread notifications.
          </div>
        )}
        <div className="space-y-2">
          {(data.data || []).map(n => (
            <div key={n._id} className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 flex items-start gap-3">
              <span className="text-lg shrink-0">{severityIcon[n.severity] || 'ℹ️'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{n.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>
                <p className="text-xs text-gray-600 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
              </div>
              <button onClick={() => markRead(n._id)} className="text-xs text-green-400 hover:text-green-300 shrink-0">Mark read</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: Performance
// ════════════════════════════════════════════════════════════
function PerformanceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi().get('/performance');
      setData(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return <div className="text-gray-400 p-4">Loading performance data...</div>;

  const dbColor = data?.dbStatus === 'fast' ? '#10B981' : data?.dbStatus === 'slow' ? '#EF4444' : '#06C8E8';

  return (
    <div className="space-y-6">
      <SectionHeader title="Performance Monitor" onRefresh={load} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">DB Latency</p>
          <p className="text-2xl font-bold" style={{ color: dbColor }}>{data?.dbLatencyMs || '--'}<span className="text-sm text-gray-400">ms</span></p>
          <Badge status={data?.dbStatus || 'unknown'} />
        </div>
        <StatCard label="Heap Memory" value={(data?.memory?.heapUsedMB || 0) + ' MB'} sub={'RSS: ' + (data?.memory?.rssMB || 0) + ' MB'} color="#8B5CF6" />
        <StatCard label="Tx Last Hour" value={(data?.activity?.txLastHour || 0).toLocaleString()} icon="📊" color="#06C8E8" />
        <StatCard label="Tx Today" value={(data?.activity?.txToday || 0).toLocaleString()} icon="📈" color="#10B981" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Process Info</p>
          <div className="space-y-2 text-sm">
            {[['PID', data?.pid],['Node Version', data?.nodeVersion],['Uptime', data?.uptimeSeconds ? Math.round(data.uptimeSeconds / 60) + ' minutes' : '--']].map(([k,v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-gray-400">{k}</span>
                <span className="text-white font-mono">{v || '--'}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Slow Requests</p>
          {(!data?.slowRequests || data.slowRequests.length === 0) ? (
            <p className="text-green-400 text-sm">No slow requests detected</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {data.slowRequests.map(r => (
                <div key={r._id} className="text-xs flex gap-2 border-b border-gray-800 pb-1">
                  <span className="text-yellow-400">{r.meta?.duration}ms</span>
                  <span className="text-gray-300 truncate">{r.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-xs text-gray-400">
        <p className="font-semibold text-gray-300 mb-2">Performance Tips</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>DB latency under 50ms is excellent. Over 200ms suggests index issues.</li>
          <li>Heap memory over 400MB on this instance may indicate a memory leak.</li>
          <li>Transaction volume spikes may impact response times.</li>
        </ul>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN: OperationsCenter
// ════════════════════════════════════════════════════════════
export default function OperationsCenter() {
  const [tab, setTab] = useState('overview');
  const [alertCount, setAlertCount] = useState(0);

  // Poll for alert count
  useEffect(() => {
    const check = async () => {
      try {
        const r = await authApi().get('/summary');
        const d = r.data.data;
        setAlertCount((d?.alerts?.length || 0) + (d?.counts?.unreadNotifs || 0));
      } catch {}
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">Operations & Monitoring Center</h1>
            <p className="text-xs text-gray-400 mt-0.5">Real-time platform health, payments, and system monitoring</p>
          </div>
          <div className="flex items-center gap-3">
            {alertCount > 0 && (
              <span className="bg-red-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full animate-pulse">
                {alertCount} alert{alertCount !== 1 ? 's' : ''}
              </span>
            )}
            <a href="/admin" className="text-xs text-cyan-400 hover:text-cyan-300">Back to Admin</a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 flex-wrap bg-gray-900 rounded-xl p-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (tab === t.id ? 'bg-cyan-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800')}>
              {t.label}
              {t.id === 'notifications' && alertCount > 0 && (
                <span className="ml-1 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">{alertCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-gray-900 rounded-xl p-5">
          {tab === 'overview'      && <OverviewTab />}
          {tab === 'payments'      && <PaymentsTab />}
          {tab === 'financial'     && <FinancialTab />}
          {tab === 'security'      && <SecurityTab />}
          {tab === 'logs'          && <LogsTab />}
          {tab === 'backup'        && <BackupTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'performance'   && <PerformanceTab />}
        </div>

        <p className="text-xs text-gray-600 text-center">Auto-refreshes every 30s on health/overview. Every 15s on performance. Click Refresh on any tab for immediate update.</p>
      </div>
    </div>
  );
}
